import {memo, useRef, useMemo, type ReactNode, type ReactElement} from 'react'
import {TreeView} from '@primer/react'
import {PortalTooltip} from '@github-ui/portal-tooltip/portalled'
import type {DiffDelta, DirectoryNode, FileNode} from './diff-file-tree-helpers'
import {caseFirstCompare, getFileTree} from './diff-file-tree-helpers'
import {useFileTreeTooltip} from '@github-ui/use-file-tree-tooltip'
import {File, type FileProps} from './pull-requests-diff-file-tree/File'

interface DirectoryProps extends Pick<FileProps, 'onSelect'> {
  directory: DirectoryNode<DiffDelta>
  depth?: number
  /**
   * Overrides the default render of <File> to allow for data modification or a custom element.
   * When using this, one should try to return a <File> element from this file or something analogous in DOM structure
   */
  fileNodeRenderer?: (props: FileProps) => ReactNode
  leadingPath?: string
  /**
   * Sorts the files and directories in the order they were provided in the `diffs` prop when true.
   * If false, performs client-side sorting alphabetically with uppercase letters first.
   */
  sortByDiffOrder: boolean
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const Directory = memo(function Directory({
  directory,
  depth = 0,
  leadingPath = '',
  sortByDiffOrder,
  onSelect,
  fileNodeRenderer,
}: DirectoryProps): ReactElement {
  const pathPrefix = leadingPath ? `${leadingPath}/` : ''

  const rowRef = useRef<HTMLElement | null>(null)

  // Using listItemRef to fix a bug where two tooltips will stay rendered at the same time
  const listItemRef = useRef<HTMLElement>(null)
  const showTooltip = useFileTreeTooltip({focusRowRef: listItemRef, mouseRowRef: rowRef})

  // collapse this directory if it has no files and only one child directory
  if (!directory.files.length && directory.directories.length === 1) {
    return (
      <>
        {directory.directories.map(subDirectory => (
          <Directory
            key={subDirectory.path}
            /* Set depth to one for top-level directories that are collapsed so that tree styling is applied correctly */
            depth={depth === 0 ? 1 : depth}
            directory={subDirectory}
            leadingPath={`${pathPrefix}${directory.name}`}
            sortByDiffOrder={sortByDiffOrder}
            onSelect={onSelect}
            fileNodeRenderer={fileNodeRenderer}
          />
        ))}
      </>
    )
  }

  function renderDirectoryContents() {
    return (
      <DirectoryRendering
        directory={directory}
        depth={depth}
        sortByDiffOrder={sortByDiffOrder}
        onSelect={onSelect}
        fileNodeRenderer={fileNodeRenderer}
      />
    )
  }

  if (depth === 0) {
    return renderDirectoryContents()
  }

  return (
    <TreeView.Item ref={listItemRef} key={directory.path} defaultExpanded id={directory.path}>
      <TreeView.LeadingVisual>
        <TreeView.DirectoryIcon />
      </TreeView.LeadingVisual>
      <span ref={rowRef}>{`${pathPrefix}${directory.name}`}</span>
      {showTooltip && (
        <PortalTooltip
          data-testid={`${directory.name}-directory-item-tooltip`}
          id={`${directory.name}-directory-item-tooltip`}
          contentRef={listItemRef}
          aria-label={`${pathPrefix}${directory.name}`}
          open
          direction="ne"
        />
      )}
      <TreeView.SubTree>{renderDirectoryContents()}</TreeView.SubTree>
    </TreeView.Item>
  )
})

type MergedEntries = {
  path: string
  pathType: string
  node: FileNode<DiffDelta> | DirectoryNode<DiffDelta>
  sortOrder: number
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const DirectoryRendering = memo(function DirectoryRendering({
  directory,
  fileNodeRenderer,
  sortByDiffOrder,
  depth = 0,
  onSelect,
}: DirectoryProps) {
  // Memoize the merged and sorted paths to prevent re-sorting on every render
  const mergedPaths: MergedEntries[] = useMemo(() => {
    // merge files and directories into a single array
    const paths: MergedEntries[] = directory.files
      .map(file => {
        return {path: file.filePath, pathType: 'file', node: file, sortOrder: file.sortOrder}
      })
      .concat(
        directory.directories.map(dir => {
          return {
            path: dir.path,
            pathType: 'directory',
            node: dir as unknown as FileNode<DiffDelta>,
            sortOrder: dir.sortOrder,
          }
        }),
      )

    if (sortByDiffOrder) {
      paths.sort((a, b) => {
        return a.sortOrder - b.sortOrder
      })
    } else {
      // sort the merged paths by path to render them in alphabetical order
      paths.sort((a, b) => caseFirstCompare(a.path, b.path))
    }

    return paths
  }, [directory.files, directory.directories, sortByDiffOrder])

  return (
    <>
      {mergedPaths.map(entry => {
        if (entry.pathType === 'file') {
          if (fileNodeRenderer) {
            return fileNodeRenderer({onSelect, file: entry.node as FileNode<DiffDelta>, depth: depth + 1})
          }
          return (
            <File key={entry.path} depth={depth + 1} file={entry.node as FileNode<DiffDelta>} onSelect={onSelect} />
          )
        } else {
          return (
            <Directory
              key={entry.path}
              depth={depth + 1}
              directory={entry.node as DirectoryNode<DiffDelta>}
              fileNodeRenderer={fileNodeRenderer}
              sortByDiffOrder={sortByDiffOrder}
              onSelect={onSelect}
            />
          )
        }
      })}
    </>
  )
})

export interface PullRequestDiffFileTreeProps
  extends Pick<FileProps, 'onSelect'>, Pick<DirectoryProps, 'fileNodeRenderer' | 'sortByDiffOrder'> {
  diffs: Readonly<Array<Readonly<DiffDelta>>>
  className?: string
}

export const PullRequestDiffFileTree = memo(function FileTree({
  diffs,
  fileNodeRenderer,
  sortByDiffOrder,
  className,
  onSelect,
}: PullRequestDiffFileTreeProps) {
  const fileTree = useMemo(() => getFileTree<DiffDelta>(diffs), [diffs])

  return (
    <TreeView aria-label="File Tree" className={className}>
      <Directory
        directory={fileTree}
        fileNodeRenderer={fileNodeRenderer}
        sortByDiffOrder={sortByDiffOrder}
        onSelect={onSelect}
      />
    </TreeView>
  )
})
